import React from "react";
import { Check, Plus, Trash2 } from "lucide-react";
import { formatEUR, formatMoney } from "@/lib/format";
import { CLASS_COLOR, CurrencyToggle, MINT, RED, TEAL, TerminalInput, ccyOptions, plColor, pctStr } from "./shared";

// Lista posizioni con editing inline. Presentazionale: stato/azioni nel parent.
export const HoldingsList = ({
  holdings,
  editingId,
  editForm,
  setEditForm,
  busy,
  onAdd,
  onOpenEdit,
  onCloseEdit,
  onSave,
  onRemove,
}) => (
  <>
    <div className="mb-2 mt-5 flex items-center justify-between">
      <p className="caps-tag text-white/45">I tuoi asset</p>
      <button
        onClick={onAdd}
        data-testid="investments-add-button"
        className="mono flex h-8 items-center gap-1.5 rounded-[6px] border px-2.5 text-[10px] font-medium uppercase tracking-[0.12em] active:scale-[0.98]"
        style={{ borderColor: `${TEAL}44`, color: TEAL }}
      >
        <Plus size={13} /> Aggiungi
      </button>
    </div>

    <div className="space-y-2" data-testid="investments-list">
      {holdings.map((h, i) => {
        const color = CLASS_COLOR[h.asset_class] || "#888";
        const isEditing = editingId === h.id;
        return (
          <div key={h.id} className="rounded-[10px] border border-white/[0.06] bg-[#0D0D14]" data-testid={`holding-row-${i}`}>
            <button onClick={() => (isEditing ? onCloseEdit() : onOpenEdit(h))} className="flex w-full items-center gap-3 px-3 py-3 text-left">
              <span
                className="mono flex h-9 w-9 shrink-0 items-center justify-center rounded-[7px] border text-[11px] font-medium"
                style={{ borderColor: `${color}55`, color, background: `${color}12` }}
              >
                {(h.symbol || "?").slice(0, 4)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-light text-white/90" data-testid="holding-symbol">
                  {h.symbol} <span className="text-white/35">{h.name}</span>
                </p>
                <div className="mono mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] tracking-[0.03em] text-white/35">
                  <span>
                    {Number(h.quantity).toLocaleString("it-IT", { maximumFractionDigits: 8 })} @ {formatMoney(h.avg_price, h.avg_price_currency)}
                  </span>
                  {h.broker && (
                    <span
                      className="rounded-[4px] border border-white/10 px-1.5 py-px text-[9px] uppercase tracking-[0.08em] text-white/45"
                      data-testid="holding-broker"
                    >
                      {h.broker}
                    </span>
                  )}
                  {h.is_manual && (
                    <span className="text-[9px] uppercase tracking-[0.08em]" style={{ color: MINT }} data-testid="holding-manual-badge">
                      · manuale
                    </span>
                  )}
                  {!h.priced && !h.is_manual && (
                    <span style={{ color: "#FFD60A" }} data-testid="holding-na">· prezzo n/d</span>
                  )}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <p className="mono text-[14px] font-medium tabular-nums text-white/90" data-testid="holding-value">{formatEUR(h.value)}</p>
                <p className="mono text-[11px] tabular-nums" style={{ color: plColor(h.pl) }} data-testid="holding-pl">
                  {pctStr(h.pl_pct)}
                </p>
              </div>
            </button>

            {isEditing && (
              <div className="border-t border-white/[0.06] p-3" data-testid={`holding-edit-${i}`}>
                <div className="flex gap-2">
                  <TerminalInput label="Quantità" value={editForm.quantity} onChange={(v) => setEditForm((f) => ({ ...f, quantity: v }))} testid="edit-quantity-input" />
                  <TerminalInput
                    label="Prezzo medio"
                    value={editForm.avg_price}
                    onChange={(v) => setEditForm((f) => ({ ...f, avg_price: v }))}
                    suffix={
                      <CurrencyToggle
                        value={editForm.avg_price_currency}
                        onChange={(c) => setEditForm((f) => ({ ...f, avg_price_currency: c }))}
                        options={ccyOptions(editForm.avg_price_currency)}
                      />
                    }
                    testid="edit-avgprice-input"
                  />
                </div>
                <div className="mt-3 flex gap-2">
                  <TerminalInput
                    label="Broker / Conto"
                    type="text"
                    value={editForm.broker}
                    onChange={(v) => setEditForm((f) => ({ ...f, broker: v }))}
                    placeholder="es. Fineco"
                    testid="edit-broker-input"
                  />
                  <TerminalInput
                    label="Prezzo manuale"
                    value={editForm.manual_price}
                    onChange={(v) => setEditForm((f) => ({ ...f, manual_price: v }))}
                    placeholder="vuoto = live"
                    suffix="€"
                    testid="edit-manualprice-input"
                  />
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => onRemove(h.id)}
                    disabled={busy}
                    data-testid="holding-delete-button"
                    className="mono flex h-10 items-center justify-center gap-1.5 rounded-[6px] border border-[#FF4D4D]/40 px-3 text-[11px] font-medium uppercase tracking-[0.12em] text-[#FF4D4D] active:scale-[0.98] disabled:opacity-50"
                  >
                    <Trash2 size={13} /> Elimina
                  </button>
                  <button
                    onClick={() => onSave(h.id)}
                    disabled={busy}
                    data-testid="holding-save-button"
                    className="mono flex h-10 flex-1 items-center justify-center gap-1.5 rounded-[6px] border bg-transparent text-[11px] font-medium uppercase tracking-[0.14em] active:scale-[0.98] disabled:opacity-50"
                    style={{ borderColor: `${TEAL}55`, color: TEAL }}
                  >
                    <Check size={13} /> Salva
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  </>
);
